import { generateInkeepToken } from '@/sdk/sdk.gen';
import { fetchWithAuthRetry } from './fetch-with-retry';

export interface ApiContext {
  agentId: string;
  subAgentId: string;
  tenantId: string;
  userEmail?: string;
  token?: string;
  /**
   * Injected by SupportChat. Called on 401 to refresh the Inkeep JWT; resolves
   * with the new token, which is reused for the retry and also stored in
   * React state so subsequent requests pick it up.
   */
  onAuthFailure?: () => Promise<string>;
}

interface MessageAction {
  label: string;
  action: string;
  variant?: 'primary' | 'muted';
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'csm';
  content: string;
  createdAt?: string;
  actions?: MessageAction[];
  /** Display name of the sender (for CSM messages — actual person's name from Slack). */
  authorName?: string;
  /** Files attached to this message (shown in user bubbles). */
  files?: FileAttachment[];
}

interface PollMessage {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  /** Name of the CSM / support agent who sent the message (populated from Slack identity). */
  authorName?: string;
  /** Inline action buttons (e.g. propose-close from CSM). */
  actions?: MessageAction[];
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function fetchInkeepToken(): Promise<string> {
  const { data, error } = await generateInkeepToken({ throwOnError: false });
  if (error || !data) throw new Error(`Token fetch failed: ${JSON.stringify(error) ?? 'unknown'}`);
  return data.token;
}

function apiHeaders(ctx: ApiContext): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-inkeep-tenant-id': ctx.tenantId,
    'x-inkeep-project-id': 'r41-support',
    'x-inkeep-agent-id': ctx.agentId,
    'x-inkeep-sub-agent-id': ctx.subAgentId,
    'x-inkeep-client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    'x-inkeep-client-timestamp': new Date().toISOString(),
    'x-r41-page-path': window.location.pathname,
  };
  if (ctx.userEmail) {
    headers['x-inkeep-run-as-user-id'] = ctx.userEmail;
  }
  if (ctx.token) {
    headers['Authorization'] = `Bearer ${ctx.token}`;
  }
  return headers;
}

export interface FileAttachment {
  name: string;
  mimeType: string;
  dataUri: string; // data:<mime>;base64,...
}

export async function callInkeepSSE(
  baseUrl: string,
  ctx: ApiContext,
  convId: string | null,
  message: string,
  onChunk?: (text: string) => void,
  onContentDone?: () => void,
  files?: FileAttachment[],
): Promise<{ convId: string; text: string }> {
  // Build message content: plain string if no files, array of parts if files attached
  let content: string | Array<Record<string, unknown>> = message;
  if (files && files.length > 0) {
    const parts: Array<Record<string, unknown>> = [
      { type: 'text', text: message },
      ...files.map((f) => ({
        type: 'image_url',
        image_url: { url: f.dataUri },
      })),
    ];
    content = parts;
  }

  const body: Record<string, unknown> = {
    model: `r41-support/${ctx.agentId}`,
    messages: [{ role: 'user', content }],
    stream: true,
  };
  if (convId) body.conversationId = convId;

  const resp = await fetch(`${baseUrl}/run/v1/chat/completions`, {
    method: 'POST',
    headers: apiHeaders(ctx),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!resp.ok) throw new Error(`Chat request failed: ${resp.status}`);

  const reader = resp.body?.getReader();
  if (!reader) throw new Error('No body');

  const decoder = new TextDecoder();
  let fullText = '';
  let resultConvId = convId || '';
  let lastError = '';
  let contentDoneFired = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    for (const line of decoder.decode(value, { stream: true }).split('\n')) {
      if (!line.startsWith('data: ') || line === 'data: [DONE]') continue;
      try {
        const data = JSON.parse(line.slice(6));
        // Detect when the LLM has finished generating content
        const finishReason = data.choices?.[0]?.finish_reason;
        if (finishReason && !contentDoneFired) {
          contentDoneFired = true;
          onContentDone?.();
        }
        const c = data.choices?.[0]?.delta?.content || '';
        if (c.startsWith('{"type":"data-operation"')) {
          try {
            const op = JSON.parse(c);
            if (op.data?.details?.conversationId) {
              resultConvId = op.data.details.conversationId;
            }
            if (op.data?.type === 'error' && op.data?.message) {
              lastError = op.data.message;
            }
          } catch {
            // Malformed data-operation chunk — non-critical, skip
          }
        } else if (c.startsWith('{"type":')) {
          // All structured agent-runtime events (tool-call, tool-output-available,
          // tool-output-error, start-step, finish-step, reasoning, etc.) are
          // internal. Swallow anything that looks like a typed JSON event so no
          // raw JSON ever reaches the chat bubble. Poll route is authoritative for
          // escalation state.
        } else if (c) {
          fullText += c;
          onChunk?.(fullText.trimStart());
        }
      } catch {
        // Malformed SSE data line — expected with chunked transfer, skip
      }
    }
  }

  // If stream produced no text but had errors, throw so the caller can handle it
  if (!fullText.trim() && lastError) {
    throw new Error(lastError);
  }

  return { convId: resultConvId, text: fullText.trim() };
}

async function authRetryingFetch(
  baseUrl: string,
  ctx: ApiContext,
  path: string,
  extraInit: RequestInit = {},
): Promise<Response> {
  if (!ctx.onAuthFailure) {
    return fetch(`${baseUrl}${path}`, {
      ...extraInit,
      headers: { ...apiHeaders(ctx), ...(extraInit.headers as Record<string, string> | undefined) },
      credentials: 'include',
    });
  }
  return fetchWithAuthRetry(
    (token) => ({
      url: `${baseUrl}${path}`,
      init: {
        ...extraInit,
        headers: {
          ...apiHeaders({ ...ctx, token }),
          ...(extraInit.headers as Record<string, string> | undefined),
        },
        credentials: 'include',
      },
    }),
    ctx.token,
    { onAuthFailure: ctx.onAuthFailure },
  );
}

export async function pollMessages(
  baseUrl: string,
  ctx: ApiContext,
  convId: string,
): Promise<{ messages: PollMessage[]; total: number; status?: string; lastReadBySupportAt?: string; ticketNumber?: number; slackThreadTs?: string }> {
  const resp = await authRetryingFetch(baseUrl, ctx, `/run/v1/poll/${convId}`);
  if (!resp.ok) throw new Error(`Poll failed: ${resp.status}`);
  return resp.json();
}

/** Post a user message to an open ticket without triggering the AI agent.
 *  Supports optional file attachments — images are sent as image_url parts. */
export async function postMessage(
  baseUrl: string,
  ctx: ApiContext,
  convId: string,
  content: string,
  files?: FileAttachment[],
): Promise<void> {
  const body: Record<string, unknown> = { role: 'user' };

  if (files && files.length > 0) {
    body.parts = [
      ...(content ? [{ type: 'text', text: content }] : []),
      ...files.map((f) => ({ type: 'image_url', image_url: { url: f.dataUri }, name: f.name })),
    ];
  } else {
    body.content = content;
  }

  const resp = await authRetryingFetch(baseUrl, ctx, `/run/v1/poll/${convId}/messages`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`Post message failed: ${resp.status}`);
}

export async function fetchConversations(
  baseUrl: string,
  ctx: ApiContext,
): Promise<ConversationSummary[]> {
  const resp = await authRetryingFetch(baseUrl, ctx, `/run/v1/conversations?limit=50`);
  if (!resp.ok) throw new Error(`Fetch conversations failed: ${resp.status}`);
  const data = await resp.json();
  return data.conversations ?? data.data ?? [];
}

/** Resolve a conversation (client-initiated close). */
export async function resolveConversation(
  baseUrl: string,
  ctx: ApiContext,
  convId: string,
): Promise<void> {
  const resp = await fetch(`${baseUrl}/run/v1/poll/${convId}/resolve`, {
    method: 'POST',
    headers: apiHeaders(ctx),
    credentials: 'include',
    body: JSON.stringify({}),
  });
  if (!resp.ok) throw new Error(`Resolve failed: ${resp.status}`);
}

/** Notify backend that the user declined ticket closure (keeps ticket open). */
export async function keepTicketOpen(
  baseUrl: string,
  ctx: ApiContext,
  convId: string,
): Promise<void> {
  const resp = await fetch(`${baseUrl}/run/v1/poll/${convId}/keep-open`, {
    method: 'POST',
    headers: apiHeaders(ctx),
    credentials: 'include',
    body: JSON.stringify({}),
  });
  if (!resp.ok) throw new Error(`Keep-open failed: ${resp.status}`);
}

/** Convert a PollMessage from the API into a local Message. */
export function pollMessageToLocal(msg: PollMessage): Message {
  const role = msg.role === 'csm' ? 'csm'
    : msg.role === 'user' ? 'user'
    : msg.role === 'system' ? 'system'
    : 'assistant';
  return { id: msg.id, role, content: msg.content, createdAt: msg.createdAt, authorName: msg.authorName, actions: msg.actions };
}
